/**
 * Servizio di autenticazione.
 *
 * Astrae il provider così che il resto dell'applicazione non sappia se sta
 * usando Supabase Auth o la modalità dimostrativa. È anche il confine da cui
 * passare per introdurre in futuro l'autenticazione a due fattori o un provider
 * aziendale.
 */

import { isDemoMode } from '@/config/env';
import { getGateway, setDemoUserId, type DataGateway } from '@/lib/data';
import type { MyContext, Profile } from '@/types/domain';

export interface AuthenticatedUser {
  id: string;
  email: string;
  fullName: string;
}

export class AuthError extends Error {
  // `cause` esiste già su Error dalla ES2022: va dichiarata come override.
  override readonly cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'AuthError';
    this.cause = cause;
  }
}

interface SupabaseAuthClient {
  auth: {
    signInWithPassword(credentials: { email: string; password: string }): Promise<{
      data: { user: { id: string; email?: string } | null };
      error: { message: string } | null;
    }>;
    signUp(credentials: {
      email: string;
      password: string;
      options?: { data?: Record<string, unknown>; emailRedirectTo?: string };
    }): Promise<{ data: { user: { id: string } | null }; error: { message: string } | null }>;
    signInWithOAuth(credentials: {
      provider: 'google';
      options?: { redirectTo?: string; queryParams?: Record<string, string> };
    }): Promise<{ error: { message: string } | null }>;
    signOut(): Promise<{ error: { message: string } | null }>;
    getUser(): Promise<{
      data: { user: { id: string; email?: string } | null };
      error: { message: string } | null;
    }>;
    resetPasswordForEmail(email: string, options?: { redirectTo?: string }): Promise<{
      error: { message: string } | null;
    }>;
    updateUser(attributes: { password?: string }): Promise<{ error: { message: string } | null }>;
    onAuthStateChange(
      callback: (event: string, session: unknown) => void,
    ): { data: { subscription: { unsubscribe(): void } } };
  };
}

function asSupabase(gateway: DataGateway): SupabaseAuthClient {
  const candidate = gateway as unknown as { client?: SupabaseAuthClient };
  if (!candidate.client) {
    throw new AuthError('Il gateway attivo non espone un provider di autenticazione.');
  }
  return candidate.client;
}

/** Chiave della sessione dimostrativa, conservata per sopravvivere al refresh. */
const DEMO_SESSION_KEY = 'bacco.demo.userId';

// -----------------------------------------------------------------------------
// API pubblica
// -----------------------------------------------------------------------------

export async function signIn(email: string, password: string): Promise<AuthenticatedUser> {
  const gateway = await getGateway();

  if (isDemoMode) {
    // In modalità dimostrativa non esiste una verifica di password: il database
    // gira nel browser e non c'è alcun segreto da proteggere. L'interfaccia lo
    // dichiara esplicitamente nella schermata di accesso.
    //
    // La ricerca passa da `demo_profiles()` e non da una SELECT su `profiles`:
    // prima del login la sessione è anonima e la Row-Level Security — a ragione —
    // le nega la lettura dell'anagrafica utenti.
    const profiles = await listDemoProfiles();
    const wanted = email.trim().toLowerCase();
    const profile = profiles.find((entry) => entry.email.toLowerCase() === wanted);
    if (!profile) {
      throw new AuthError('Nessun utente dimostrativo con questa email.');
    }
    setDemoUserId(profile.id);
    window.localStorage.setItem(DEMO_SESSION_KEY, profile.id);
    return { id: profile.id, email: profile.email, fullName: profile.full_name };
  }

  const client = asSupabase(gateway);
  const { data, error } = await client.auth.signInWithPassword({ email: email.trim(), password });
  if (error || !data.user) {
    // Il messaggio resta generico: distinguere "utente inesistente" da "password
    // errata" consentirebbe di enumerare gli account registrati.
    throw new AuthError('Credenziali non valide.', error);
  }

  const profile = await gateway.selectOne<Profile>('profiles', {
    filters: [{ column: 'id', operator: 'eq', value: data.user.id }],
  });
  return {
    id: data.user.id,
    email: data.user.email ?? email,
    fullName: profile?.full_name ?? '',
  };
}

/**
 * Avvia l'accesso con Google. Il browser lascia Bacco e vi ritorna su
 * `/accedi` con la sessione: la raccoglie il client Supabase all'avvio.
 */
export async function signInWithGoogle(): Promise<void> {
  if (isDemoMode) {
    throw new AuthError("L'accesso con Google non è disponibile in modalità dimostrativa.");
  }
  const gateway = await getGateway();
  const { error } = await asSupabase(gateway).auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/accedi`,
      // Con più account Google sul dispositivo si sceglie ogni volta quale usare:
      // su un tablet condiviso di sala è ciò che evita di entrare come il collega.
      queryParams: { prompt: 'select_account' },
    },
  });
  if (error) throw new AuthError('Non è stato possibile avviare l’accesso con Google.', error);
}

/**
 * Trasforma in appartenenze gli inviti rivolti all'indirizzo dell'utente.
 * Invocata a ogni avvio della sessione; se non ci sono inviti non fa nulla.
 */
export async function acceptPendingInvitations(): Promise<number> {
  if (isDemoMode) return 0;
  const gateway = await getGateway();
  const result = await gateway.rpc<{ accepted: number } | null>('accept_my_invitations');
  return result?.accepted ?? 0;
}

/** Vero se l'utente amministra la piattaforma. La RLS gli lascia leggere solo la propria riga. */
export async function checkPlatformAdmin(userId: string): Promise<boolean> {
  const gateway = await getGateway();
  const rows = await gateway.select<{ user_id: string }>('platform_admins', {
    filters: [{ column: 'user_id', operator: 'eq', value: userId }],
  });
  return rows.length > 0;
}

export async function signUp(email: string, password: string, fullName: string): Promise<void> {
  if (isDemoMode) {
    throw new AuthError('La registrazione non è disponibile in modalità dimostrativa.');
  }
  const gateway = await getGateway();
  const client = asSupabase(gateway);
  const { error } = await client.auth.signUp({
    email: email.trim(),
    password,
    options: {
      data: { full_name: fullName.trim() },
      emailRedirectTo: `${window.location.origin}/auth/conferma`,
    },
  });
  if (error) throw new AuthError(error.message, error);
}

export async function signOut(): Promise<void> {
  const gateway = await getGateway();
  if (isDemoMode) {
    setDemoUserId(null);
    window.localStorage.removeItem(DEMO_SESSION_KEY);
    return;
  }
  const { error } = await asSupabase(gateway).auth.signOut();
  if (error) throw new AuthError(error.message, error);
}

export async function requestPasswordReset(email: string): Promise<void> {
  if (isDemoMode) {
    throw new AuthError('Il recupero password non è disponibile in modalità dimostrativa.');
  }
  const gateway = await getGateway();
  const { error } = await asSupabase(gateway).auth.resetPasswordForEmail(email.trim(), {
    redirectTo: `${window.location.origin}/auth/nuova-password`,
  });
  // Nessun errore viene propagato al chiamante: rivelare se l'indirizzo esiste
  // permetterebbe di enumerare gli account. L'interfaccia mostra sempre lo
  // stesso messaggio di conferma.
  if (error) console.warn('Recupero password non riuscito:', error.message);
}

export async function updatePassword(newPassword: string): Promise<void> {
  if (isDemoMode) {
    throw new AuthError('Il cambio password non è disponibile in modalità dimostrativa.');
  }
  const gateway = await getGateway();
  const { error } = await asSupabase(gateway).auth.updateUser({ password: newPassword });
  if (error) throw new AuthError(error.message, error);
}

/** Recupera la sessione attiva, se esiste. Invocata all'avvio dell'applicazione. */
export async function getCurrentUser(): Promise<AuthenticatedUser | null> {
  const gateway = await getGateway();

  if (isDemoMode) {
    const storedId = window.localStorage.getItem(DEMO_SESSION_KEY);
    if (!storedId) return null;
    // L'identità va impostata *prima* della lettura: da quel momento la sessione
    // non è più anonima e la RLS consente al profilo di leggere sé stesso.
    setDemoUserId(storedId);
    const profile = await gateway.selectOne<Profile>('profiles', {
      filters: [{ column: 'id', operator: 'eq', value: storedId }],
    });
    if (!profile) {
      window.localStorage.removeItem(DEMO_SESSION_KEY);
      setDemoUserId(null);
      return null;
    }
    return { id: profile.id, email: profile.email, fullName: profile.full_name };
  }

  const { data } = await asSupabase(gateway).auth.getUser();
  if (!data.user) return null;

  const profile = await gateway.selectOne<Profile>('profiles', {
    filters: [{ column: 'id', operator: 'eq', value: data.user.id }],
  });
  return {
    id: data.user.id,
    email: data.user.email ?? profile?.email ?? '',
    fullName: profile?.full_name ?? '',
  };
}

/** Contesto applicativo: organizzazioni, ruolo e permessi dell'utente. */
export async function loadContexts(): Promise<MyContext[]> {
  const gateway = await getGateway();
  return gateway.select<MyContext>('v_my_context');
}

export interface DemoProfile {
  id: string;
  email: string;
  full_name: string;
  role_name: string;
  role_code: string;
}

/**
 * Elenco dei profili dimostrativi mostrato nella schermata di accesso.
 *
 * Si appoggia a `public.demo_profiles()`, definita **solo** nel bootstrap della
 * modalità dimostrativa: non esiste nelle migrazioni di produzione, dove
 * consentirebbe a un anonimo di enumerare gli utenti.
 */
export async function listDemoProfiles(): Promise<DemoProfile[]> {
  if (!isDemoMode) return [];
  const gateway = await getGateway();
  return (await gateway.rpc<DemoProfile[] | null>('demo_profiles')) ?? [];
}
